import { useState } from 'react';
import { Switch } from 'antd';
import { DeleteOutlined, EditOutlined, MailOutlined, PlusOutlined, SearchOutlined, SendOutlined, StarFilled, StarOutlined } from '@ant-design/icons';

import { DataTable, MenuThaoTac, Tile } from '../../../../components/shared';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiRpa } from '../chung';
import { LOAI_TAI_KHOAN_HIEN } from '../../constants';
import { useLuuTaiKhoanGui, useTaiKhoanGui, useXoaTaiKhoanGui } from '../../hooks/useThongBao';
import { LOAI_TAI_KHOAN, locTaiKhoan, thanDoiNhanh } from '../../nghiep-vu/thong-bao';
import type { BoLocTaiKhoan } from '../../nghiep-vu/thong-bao';
import type { TaiKhoanGui } from '../../types';
import { HopTaiKhoan } from './HopTaiKhoan';

const CO_TRANG = [10, 20, 50, 100];
const LOC_TRONG: BoLocTaiKhoan = { tim: '', loai: '', trangThai: '' };

type Hop = { kieu: 'form'; goc: TaiKhoanGui | null } | { kieu: 'xoa'; goc: TaiKhoanGui } | null;

/**
 * Tab Tài khoản gửi (`RpaSenderAccountsView` của native): Gmail SMTP và Telegram Bot máy chủ RPA dùng để gửi thông
 * báo. Bí mật không bao giờ trả về, chỉ `hasSecret`. Sửa theo `rpa:tai-khoan-gui:update`.
 */
export function TabTaiKhoanGui({ suaDuoc, docProjectDuoc }: { suaDuoc: boolean; docProjectDuoc: boolean }) {
  const q = useTaiKhoanGui();
  if (q.isPending) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <Tile>
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      </Tile>
    );
  }
  return <NoiDung ds={q.data} suaDuoc={suaDuoc} docProjectDuoc={docProjectDuoc} />;
}

function NoiDung({ ds, suaDuoc, docProjectDuoc }: { ds: TaiKhoanGui[]; suaDuoc: boolean; docProjectDuoc: boolean }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const luu = useLuuTaiKhoanGui();
  const [loc, setLoc] = useState<BoLocTaiKhoan>(LOC_TRONG);
  const [hop, setHop] = useState<Hop>(null);
  const hien = locTaiKhoan(ds, loc);
  const dangDoi = (a: TaiKhoanGui) => luu.isPending && luu.variables?.id === a.id;

  const doiNhanh = (a: TaiKhoanGui, doi: { enabled?: boolean; isDefault?: boolean }, xong: string) =>
    luu.mutate(
      { id: a.id, than: thanDoiNhanh(a, doi) },
      { onSuccess: () => toast.success(xong), onError: (e) => toast.error(dichLoi(e)) },
    );

  const nutThem = suaDuoc ? (
    <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'form', goc: null })}>
      {t('rpaTaiKhoanGui.them')}
    </Button>
  ) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          allowClear
          autoComplete="off"
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('rpaTaiKhoanGui.tim')}
          aria-label={t('rpaTaiKhoanGui.tim')}
          value={loc.tim}
          onChange={(e) => setLoc((c) => ({ ...c, tim: e.target.value }))}
          className="w-full sm:w-64"
        />
        <Select<string>
          value={loc.loai}
          onChange={(loai) => setLoc((c) => ({ ...c, loai }))}
          aria-label={t('rpaTaiKhoanGui.locLoai')}
          className="w-full sm:w-48"
          options={[
            { value: '', label: t('rpaTaiKhoanGui.moiLoai') },
            ...LOAI_TAI_KHOAN.map((l) => ({ value: l, label: t(LOAI_TAI_KHOAN_HIEN[l].khoa) })),
          ]}
        />
        <Select<string>
          value={loc.trangThai}
          onChange={(trangThai) => setLoc((c) => ({ ...c, trangThai }))}
          aria-label={t('rpaThongBao.locTrangThai')}
          className="w-full sm:w-40"
          options={[
            { value: '', label: t('rpaThongBao.moiTrangThai') },
            { value: 'bat', label: t('rpa.dangBat') },
            { value: 'tat', label: t('rpa.dangTat') },
          ]}
        />
        <span className="text-xs text-content-muted" role="status">
          {t('rpaTaiKhoanGui.dem', { hien: soVN(hien.length, locale), tong: soVN(ds.length, locale) })}
        </span>
        {nutThem && <div className="ml-auto">{nutThem}</div>}
      </div>
      <p className="text-xs text-content-muted">{t('rpaTaiKhoanGui.moTa')}</p>

      <Tile className="min-w-0">
        {ds.length === 0 ? (
          <Empty
            icon={MailOutlined}
            title={t('rpaTaiKhoanGui.rong')}
            description={t('rpaTaiKhoanGui.rongMoTa')}
            action={nutThem ?? undefined}
          />
        ) : hien.length === 0 ? (
          <Empty icon={SearchOutlined} title={t('rpaTaiKhoanGui.rongLoc')} />
        ) : (
          <DataTable<TaiKhoanGui>
            caption={t('rpaCauHinh.tabTaiKhoanGui')}
            dataSource={hien}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={
              hien.length > 20
                ? {
                    defaultPageSize: 20,
                    pageSizeOptions: CO_TRANG.map(String),
                    showSizeChanger: true,
                    showTotal: (tong, [tu, den]) =>
                      t('rpaTaiKhoanGui.tongTrang', { tu: soVN(tu, locale), den: soVN(den, locale), tong: soVN(tong, locale) }),
                  }
                : false
            }
            columns={[
              { title: t('chung.stt'), key: 'stt', width: 56, render: (_, __, i) => <span className="tabular-nums">{i + 1}</span> },
              {
                title: t('rpaTaiKhoanGui.cotTen'),
                key: 'name',
                render: (_, a) => (
                  <div className="min-w-0">
                    <div className="font-medium text-content-primary">{a.name}</div>
                    <div className="text-xs text-content-muted">{t('rpaTaiKhoanGui.capNhat', { luc: ngayGioVN(a.updatedAt, locale) })}</div>
                    <div className="max-w-xs truncate text-xs text-content-muted">
                      {a.workflows.length
                        ? t('rpaTaiKhoanGui.gioiHanWorkflow', { so: soVN(a.workflows.length, locale) })
                        : t('rpaTaiKhoanGui.moiWorkflow')}
                    </div>
                  </div>
                ),
              },
              {
                title: t('rpaTaiKhoanGui.cotLoai'),
                key: 'type',
                render: (_, a) => {
                  const l = LOAI_TAI_KHOAN_HIEN[a.type];
                  return (
                    <Badge variant={l.variant} className="whitespace-nowrap">
                      {a.type === 'gmail' ? <MailOutlined aria-hidden /> : <SendOutlined aria-hidden />}
                      {t(l.khoa)}
                    </Badge>
                  );
                },
              },
              {
                title: t('rpaTaiKhoanGui.cotGuiTu'),
                key: 'from',
                render: (_, a) =>
                  a.type === 'telegram' ? (
                    <span className="text-sm text-content-secondary">
                      {t(a.hasSecret ? 'rpaTaiKhoanGui.botDaLuu' : 'rpaTaiKhoanGui.botChuaCo')}
                    </span>
                  ) : (
                    <div className="whitespace-nowrap">
                      <div className="text-sm text-content-primary">{a.email}</div>
                      <div className="text-xs text-content-muted">
                        {a.smtpHost}:{a.smtpPort}
                      </div>
                    </div>
                  ),
              },
              {
                title: t('rpaTaiKhoanGui.cotMacDinh'),
                key: 'isDefault',
                align: 'center',
                render: (_, a) =>
                  a.isDefault ? (
                    <span className="inline-flex" title={t('rpaTaiKhoanGui.laMacDinh')}>
                      <StarFilled className="text-warning-500" aria-hidden />
                      <span className="sr-only">{t('rpaTaiKhoanGui.laMacDinh')}</span>
                    </span>
                  ) : suaDuoc ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<StarOutlined />}
                      aria-label={t('rpaTaiKhoanGui.datMacDinh', { ten: a.name })}
                      title={t('rpaTaiKhoanGui.datMacDinh', { ten: a.name })}
                      disabled={!a.enabled || dangDoi(a)}
                      onClick={() => doiNhanh(a, { isDefault: true }, t('rpaTaiKhoanGui.daDatMacDinh', { ten: a.name }))}
                    />
                  ) : null,
              },
              {
                title: t('rpaTaiKhoanGui.cotBat'),
                key: 'enabled',
                render: (_, a) =>
                  suaDuoc ? (
                    <Switch
                      checked={a.enabled}
                      disabled={dangDoi(a)}
                      aria-label={t('rpaTaiKhoanGui.batTaiKhoan', { ten: a.name })}
                      onChange={(enabled) =>
                        doiNhanh(a, { enabled }, t(enabled ? 'rpaTaiKhoanGui.daBat' : 'rpaTaiKhoanGui.daTat'))
                      }
                    />
                  ) : (
                    <span className="text-sm">{t(a.enabled ? 'rpa.dangBat' : 'rpa.dangTat')}</span>
                  ),
              },
              ...(suaDuoc
                ? [
                    {
                      title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                      key: 'thaoTac',
                      width: 72,
                      align: 'center' as const,
                      fixed: 'right' as const,
                      render: (_: unknown, a: TaiKhoanGui) => (
                        <MenuThaoTac
                          tenBanGhi={a.name}
                          muc={[
                            { khoa: 'sua', nhan: t('rpaTaiKhoanGui.sua'), icon: <EditOutlined />, nhom: 'sua', onChon: () => setHop({ kieu: 'form', goc: a }) },
                            { khoa: 'xoa', nhan: t('rpaTaiKhoanGui.xoa'), icon: <DeleteOutlined />, nhom: 'pha-huy', onChon: () => setHop({ kieu: 'xoa', goc: a }) },
                          ]}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Tile>
      <p className="text-xs text-content-muted">{t('rpaTaiKhoanGui.chanTrang')}</p>

      {hop?.kieu === 'form' && <HopTaiKhoan goc={hop.goc} docProjectDuoc={docProjectDuoc} onClose={() => setHop(null)} />}
      {hop?.kieu === 'xoa' && <HopXoaTaiKhoan goc={hop.goc} onClose={() => setHop(null)} />}
    </div>
  );
}

function HopXoaTaiKhoan({ goc, onClose }: { goc: TaiKhoanGui; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const xoa = useXoaTaiKhoanGui();
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem
      tieuDe={t('rpaTaiKhoanGui.xoaTieuDe', { ten: goc.name })}
      than={t('rpaTaiKhoanGui.xoaThan')}
      nhanXacNhan={t('rpaTaiKhoanGui.xoa')}
      dangChay={xoa.isPending}
      loi={xoa.error ? dichLoi(xoa.error) : null}
      onClose={onClose}
      onXacNhan={() =>
        xoa.mutate(goc.id, {
          onSuccess: () => {
            toast.success(t('rpaTaiKhoanGui.daXoa'));
            onClose();
          },
        })
      }
    />
  );
}
